import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { FontIcon } from '@fluentui/react';
import {
  PagedDataGrid,
  type IPagedDataGridRefType,
  type DataGridColumn,
  type ICommandBarItem,
} from '@moko/ui';
import { appRoles } from '../../authConfig';
import {
  deleteGroup,
  exportGroups,
  getGroups,
  IGroup,
  importGroups,
  type IImportGroupsResponse,
} from '../../services/groupService';
import { profileDataAtom } from '../../atoms/authAtoms';
import { successMessageAtom } from '../../atoms/messageBarAtoms';
import { formatImportRowsSuccess } from '../../utils/csvHelpers';
import ImportPanel from '../../common/ImportPanel';
import ImportRowResults from '../../common/ImportRowResults';
import { useExportCsv } from '../../common/useExportCsv';

const GROUPS_COLUMNS: DataGridColumn<IGroup>[] = [
  { key: 'id', name: 'Id', minWidth: 100 },
  { key: 'name', name: 'Name', minWidth: 200 },
];

const Groups = () => {
  const profileData = useAtomValue(profileDataAtom);
  const navigate = useNavigate();
  const setSuccessMessage = useSetAtom(successMessageAtom);

  const [selectedGroupId, setSelectedGroupId] = useState<number>();
  const pagedDataGridRef = useRef<IPagedDataGridRefType>(null);

  const isAdmin = profileData.roles.includes(appRoles.Admin);

  const [importPanelOpen, setImportPanelOpen] = useState(false);
  const handleExport = useExportCsv(exportGroups, 'groups');

  const commandBarItems: ICommandBarItem[] = useMemo(
    () => [
      {
        key: 'refresh',
        text: 'Refresh',
        icon: <FontIcon iconName="Refresh" />,
        onClick: (ctx) => ctx.refresh(),
      },
      {
        key: 'newGroup',
        text: 'New',
        icon: <FontIcon iconName="Add" />,
        onClick: () => navigate('/newGroup'),
        disabled: !isAdmin,
      },
      {
        key: 'editGroup',
        text: 'Edit',
        icon: <FontIcon iconName="Edit" />,
        onClick: () => navigate(`/editGroup?id=${selectedGroupId}`),
        disabled: !isAdmin || !selectedGroupId,
      },
      {
        key: 'deleteGroup',
        text: 'Delete',
        icon: <FontIcon iconName="Delete" />,
        onClick: (ctx) => {
          if (!selectedGroupId) return;
          const abortController = new AbortController();
          deleteGroup(abortController, Number(selectedGroupId)).then(() => {
            ctx.refresh();
            setSuccessMessage(`Group with id: ${selectedGroupId} deleted.`);
          });
        },
        disabled: !isAdmin || !selectedGroupId,
      },
      {
        key: 'importGroups',
        text: 'Import',
        icon: <FontIcon iconName="CloudUpload" />,
        onClick: () => setImportPanelOpen(true),
        disabled: !isAdmin,
      },
      {
        key: 'exportGroups',
        text: 'Export',
        icon: <FontIcon iconName="CloudDownload" />,
        onClick: () => {
          void handleExport();
        },
        disabled: !isAdmin,
      },
    ],
    [isAdmin, selectedGroupId, navigate, setSuccessMessage, handleExport]
  );

  return (
    <>
      <PagedDataGrid<IGroup>
        ref={pagedDataGridRef}
        columns={GROUPS_COLUMNS}
        getAction={async (abortController, pageSize, pageNo, orderBy, search) =>
          getGroups(abortController, pageSize, pageNo, orderBy, search)
        }
        commandBarItems={commandBarItems}
        selectionMode="single"
        onSelectionChange={(selection) => {
          setSelectedGroupId(selection.id);
        }}
        onRowDoubleClicked={(selection) => {
          navigate(`/editGroup?id=${selection.id}`);
        }}
      />
      <ImportPanel<IImportGroupsResponse>
        isOpen={importPanelOpen}
        headerText="Import Groups"
        onDismiss={() => setImportPanelOpen(false)}
        importApi={importGroups}
        getErrorsCount={(r) => r.errorsCount}
        onImportSucceeded={(r) =>
          formatImportRowsSuccess(r.importGroupsResponses)
        }
        onAfterImport={() => pagedDataGridRef.current?.refresh()}
        renderResults={(r) => (
          <ImportRowResults
            rows={r.importGroupsResponses}
            messages={r.messages}
          />
        )}
      />
    </>
  );
};

export default Groups;
